import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ChromeReaderModeOutlinedIcon from '@mui/icons-material/ChromeReaderModeOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import TimelapseOutlinedIcon from '@mui/icons-material/TimelapseOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { Box, FormControlLabel, Grid } from '@mui/material';
import { IJob } from 'wisestep-api';
import {
  ChipsType,
  WsColors,
  WsIconCurrency,
  WsIconEducation,
  WsIconLikeLocation,
  WsIconLocation,
  WsIconPersons,
  WsIconPosition,
  WsIconProfile,
  WsIconTag,
  WsIconText,
  WsIconTime,
  WsIconVisa,
  WsTagsList,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import {
  getCategories,
  getJobIndustries,
} from '../../../../../features/jobs/helper/JobHelperService';
import formatJobInsights from '../../../../../utils/formatJobInsights';
import formatJobType from '../../../../../utils/formatJobType';

interface jobInfoProps {
  job: IJob;
}

const JobDetails = ({ job }: jobInfoProps) => {
  const jobCategories = getCategories();
  const jobIndustries = getJobIndustries();
  const taxTerms = job.taxTerms ? `On ${job.taxTerms}` : '';
  const jobCategory = jobCategories.find((object) => object.value == job.jobCategory);

  const jobOccupation = jobCategory?.occupations.find(
    (occupation) => occupation.value == job.jobOccupation
  );

  const jobIndustry = jobIndustries.find((industry) => industry.value == job.jobIndustryName);

  const jobInfoArray: React.ReactFragment[] = [];

  jobInfoArray.push(
    <Grid item xs={6}>
      <WsIconText
        icon={<WsIconPosition color={WsColors.GreyBlack} />}
        text={String(job.numberOfPositions ?? 'Not Available')}
        popOverText="No. Of. Positions"
        textCursor="pointer"
      />
    </Grid>
  );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconCurrency color={WsColors.GreyBlack} />}
      text={
        job.salaryCurrency && job.minimumSalary && job.maximumSalary
          ? `${job.salaryCurrency} ${job.minimumSalary} - ${job.maximumSalary} ${job.salaryTimeSpan} ${taxTerms}`
          : 'Not Available'
      }
      popOverText="Salary"
      textCursor="pointer"
    />
  );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconLikeLocation color={WsColors.GreyBlack} />}
      text={job.remoteJob ? 'Remote Available' : 'Remote Unavailable'}
      popOverText="Remote Availability"
      textCursor="pointer"
    />
  );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconEducation color={WsColors.GreyBlack} />}
      text={job?.educationRequirements ? job.educationRequirements?.join(' & ') : 'Not Available'}
      popOverText="Education Requirements"
      textCursor="pointer"
    />
  );

  jobInfoArray.push(
    <WsIconText
      icon={<DnsOutlinedIcon fontSize="small" color="disabled" />}
      text={job?.experienceRequirements ? job.experienceRequirements.join(' & ') : 'Not Available'}
      popOverText="Required Experience"
      textCursor="pointer"
    />
  );
  job.workDuration &&
    job.workDurationTimeSpan &&
    jobInfoArray.push(
      <WsIconText
        icon={<TimelapseOutlinedIcon fontSize="small" color="disabled" />}
        text={`${job.workDuration} - ${job.workDurationTimeSpan}`}
        popOverText="Work Duration"
        textCursor="pointer"
      />
    );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconTime color={WsColors.GreyBlack} />}
      text={job.jobType ? formatJobType(job.jobType) : 'Not Available'}
      popOverText="Job Type"
      textCursor="pointer"
    />
  );

  job.jobStatusType &&
    jobInfoArray.push(
      <WsIconText
        icon={<ChromeReaderModeOutlinedIcon fontSize="small" color="disabled" />}
        text={job.jobStatusType}
        popOverText="Job Status Type"
        textCursor="pointer"
      />
    );

  job.workAuthorization &&
    jobInfoArray.push(
      <WsIconText
        icon={<WsIconProfile color={WsColors.GreyBlack} />}
        text={job.workAuthorization}
        popOverText="Work Authorization"
        textCursor="pointer"
      />
    );

  jobInfoArray.push(
    <WsIconText
      icon={<WsIconVisa color={WsColors.GreyBlack} />}
      text={job.visaRequirements ?? 'Not Available'}
      popOverText="Visa Requirements"
      textCursor="pointer"
    />
  );

  jobIndustry &&
    jobInfoArray.push(
      <WsIconText
        icon={<WarehouseOutlinedIcon fontSize="small" color="disabled" />}
        text={jobIndustry.label}
        popOverText="Job Industry"
        textCursor="pointer"
      />
    );

  jobCategory &&
    jobInfoArray.push(
      <WsIconText
        icon={<CategoryOutlinedIcon fontSize="small" color="disabled" />}
        text={jobCategory?.label}
        popOverText="Job Category"
        textCursor="pointer"
      />
    );

  jobOccupation &&
    jobInfoArray.push(
      <WsIconText
        icon={<WorkOutlineIcon fontSize="small" color="disabled" />}
        text={jobOccupation.label}
        popOverText="Job Occupation"
        textCursor="pointer"
      />
    );

  job.hiringFor &&
    jobInfoArray.push(
      <WsIconText
        icon={<WsIconPersons color={WsColors.GreyBlack} />}
        text={job.hiringFor}
        popOverText="Hiring For"
        textCursor="pointer"
      />
    );
  job.url &&
    jobInfoArray.push(
      <WsIconText
        icon={<LinkOutlinedIcon fontSize="small" color="disabled" />}
        text={job.url}
        popOverText="Job URL"
        textCursor="pointer"
      />
    );

  job.jobSource &&
    jobInfoArray.push(
      <WsIconText
        icon={<SourceOutlinedIcon fontSize="small" color="disabled" />}
        text={job.jobSource}
        popOverText="Job Source"
        textCursor="pointer"
      />
    );

  job.otherBenefitDetails &&
    jobInfoArray.push(
      <WsIconText
        icon={<WsIconTag color={WsColors.GreyBlack} />}
        text={job.otherBenefitDetails}
        popOverText="Benefits"
        textCursor="pointer"
      />
    );

  const InfoLayout = () => {
    return (
      <>
        {jobInfoArray.map((item, index) => (
          <Grid item xs={4} key={index} pt={2}>
            {item}
          </Grid>
        ))}
      </>
    );
  };

  return (
    <Box m={2}>
      <Grid container spacing={2} flex-direction="column" pb={2}>
        <InfoLayout />
      </Grid>

      <>
        <FormControlLabel
          control={
            <Box pr={1}>
              <WsIconLocation />
            </Box>
          }
          label={
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              color={WsTypoColors.PRIMARY}
              cursor="pointer"
            >
              Location :
            </WsTypography>
          }
        />
        <Box py={1} px={2}>
          <WsTagsList
            chipArray={
              job?.locations?.map((location) =>
                [location.city, location.state, location.country].filter(Boolean).join(',')
              ) ?? []
            }
            count={3}
            type={ChipsType.SECONDARY}
          />
        </Box>
      </>

      <>
        <FormControlLabel
          control={
            <Box pr={1}>
              <WsIconTag />
            </Box>
          }
          label={
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              color={WsTypoColors.PRIMARY}
              cursor="pointer"
            >
              Skills :
            </WsTypography>
          }
        />
        <Box py={1} px={2}>
          <WsTagsList
            chipArray={job.skills ?? []}
            count={5}
            type={ChipsType.PRIMARY}
            borderColorFlag={true}
          />
        </Box>
      </>

      <>
        <FormControlLabel
          control={
            <Box pr={1}>
              <WsIconTag />
            </Box>
          }
          label={
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              color={WsTypoColors.PRIMARY}
              cursor="pointer"
            >
              Tags :
            </WsTypography>
          }
        />
        <Box py={1} px={2} mb={1}>
          <WsTagsList
            chipArray={job.tags ?? []}
            count={5}
            type={ChipsType.PRIMARY}
            allowDelete={true}
          />
        </Box>
      </>

      <>
        <WsTypography
          variant={WsTypoVariants.SUB_TITLE}
          color={WsTypoColors.PRIMARY}
          cursor="pointer"
        >
          Job Description
        </WsTypography>
        <Box style={{ overflowY: 'auto' }}>
          <div dangerouslySetInnerHTML={{ __html: job.description ?? '' }}></div>
        </Box>
      </>

      {job.insights && (
        <>
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            color={WsTypoColors.PRIMARY}
            cursor="pointer"
          >
            Job Insights
          </WsTypography>
          <Box p={1}>
            {formatJobInsights(job.insights).map((item, index) => (
              <Box key={index}>
                <div dangerouslySetInnerHTML={{ __html: item }}></div>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default JobDetails;
